var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
import * as Long from "long";
import { selectBalancingExpansionsByFarmId } from "../balancingSelectors/selectBalancingExpansions";
import { selectBalancingFarmsByFarmId } from "../balancingSelectors/selectBalancingFarms";
import { selectResourceInfo } from "../balancingSelectors/selectBalancingResources";
import { IdResource } from "../constants/IdResource";
import { EventType } from "../features/balancing/types/BalancingCore";
import { IdPurchaseType } from "../features/ongoing-purchase/IdPurchaseType";
import { utilGrid } from "../utils/grid/utilGrid";
import { selectActiveDiscountEventInfoByEventType } from "./selectEventInfo";
import { selectPlayerLevel } from "./selectPlayerLevelInfo";
var Point = PIXI.Point;
export var selectExpansionInfo = function (state) {
    var unlockedExpansionIds = state.baseInfo.expansionIds;
    var balancingExpansionGroup = selectBalancingExpansionsByFarmId(state.playerBase.base.baseId);
    if (balancingExpansionGroup == undefined) {
        throw new Error("Expansion Group not found baseId: " + state.playerBase.base.baseId);
    }
    var balancingBase = selectBalancingFarmsByFarmId(state.playerBase.base.baseId);
    if (balancingBase == undefined) {
        throw new Error("Base not found baseId: " + state.playerBase.base.baseId);
    }
    return {
        baseSetup: {
            expansionsSizeX: balancingExpansionGroup.xExtent,
            expansionsSizeY: balancingExpansionGroup.yExtent,
            expansionsX: balancingBase.expansionsX,
            expansionsY: balancingBase.expansionsY,
            startX: balancingBase.minX,
            startY: balancingBase.minY,
        },
        expansions: balancingExpansionGroup.expansion,
        unlockedExpansionIds: unlockedExpansionIds || undefined,
    };
};
export var selectExpansionBuyCost = function (state) {
    var expansionInfo = selectBuyExpansionInfo(state);
    var standardCost = expansionInfo.standard.cost;
    var purchaseStandardCostInfo = getExpansionResourceCosts(state, standardCost);
    var levelLockStandard = getLevelLock(state, expansionInfo, IdPurchaseType.standard);
    var missingDollars = getMissingDollars(purchaseStandardCostInfo);
    var standardCostInfo = __assign({}, levelLockStandard, { missingAmount: missingDollars, resources: purchaseStandardCostInfo });
    var premiumCost = expansionInfo.premium.cost;
    var purchasePremiumCostInfo = getExpansionResourceCosts(state, premiumCost);
    var levelLockPremium = getLevelLock(state, expansionInfo, IdPurchaseType.premium);
    var missingGold = getMissingGolds(purchasePremiumCostInfo);
    var premiumCostInfo = __assign({}, levelLockPremium, { missingAmount: missingGold, resources: purchasePremiumCostInfo });
    return {
        premium: premiumCostInfo,
        standard: standardCostInfo,
    };
};
var getLevelLock = function (state, expansionInfo, type) {
    var playerLevel = selectPlayerLevel(state);
    if (!expansionInfo[type] || !expansionInfo[type].requPlayerLevel) {
        return {
            levelLock: false,
        };
    }
    var requiredLevel = expansionInfo[type].requPlayerLevel;
    return playerLevel >= requiredLevel
        ? {
            levelLock: false,
        }
        : {
            levelLock: true,
            requiredLevel: requiredLevel,
        };
};
var getMissingDollars = function (allCosts) {
    var costDollar = allCosts.find(function (cost) { return cost.resource.id === IdResource.Money; });
    if (!costDollar || costDollar.available.greaterThanOrEqual(costDollar.cost)) {
        return undefined;
    }
    return costDollar.cost.subtract(costDollar.available);
};
var getMissingGolds = function (allCosts) {
    var costGold = allCosts.find(function (cost) { return cost.resource.id === IdResource.Gold; });
    if (!costGold || costGold.available.greaterThanOrEqual(costGold.cost)) {
        return undefined;
    }
    return costGold.cost.subtract(costGold.available);
};
var getExpansionResourceCosts = function (state, allCost) {
    var playerInventory = state.inventoryInfo.resource;
    if (!playerInventory) {
        return allCost.map(function (costs) { return ({
            available: Long.ZERO,
            cost: Long.fromValue(costs.amount),
            resource: selectResourceInfo(costs.resourceId),
        }); });
    }
    else {
        return allCost.reduce(function (costInfo, costs) {
            var playerResource = playerInventory.find(function (inventoryResource) {
                return inventoryResource.typeId === costs.resourceId;
            });
            costInfo.push({
                available: !playerResource ? Long.ZERO : playerResource.amount || Long.ZERO,
                cost: Long.fromValue(costs.amount),
                resource: selectResourceInfo(costs.resourceId),
            });
            return costInfo;
        }, []);
    }
};
export var selectBuyExpansionInfo = function (state) {
    var balancingExpansionGroup = selectBalancingExpansionsByFarmId(state.playerBase.base.baseId);
    if (balancingExpansionGroup == undefined) {
        throw new Error("Expansion Group not found baseId:" + state.playerBase.base.baseId);
    }
    var nextExpansionToBuyIndex = getUnlockedExpansionsCount(state);
    return balancingExpansionGroup.expansion[nextExpansionToBuyIndex];
};
export var selectBaseDimensions = function (state, viewport) {
    var baseSetup = selectExpansionInfo(state).baseSetup;
    var baseDimensions = utilGrid.getBaseDimensions(baseSetup);
    return {
        bottom: viewport.toGlobal(new Point(baseDimensions.bottom.x, baseDimensions.bottom.y)),
        left: viewport.toGlobal(new Point(baseDimensions.left.x, baseDimensions.left.y)),
        right: viewport.toGlobal(new Point(baseDimensions.right.x, baseDimensions.right.y)),
        top: viewport.toGlobal(new Point(baseDimensions.top.x, baseDimensions.top.y)),
    };
};
var getUnlockedExpansionsCount = function (state) {
    if (!state.baseInfo.expansionIds || state.baseInfo.expansionIds.length === 0) {
        return 0;
    }
    return state.baseInfo.expansionIds.length;
};
export var selectActiveExpansionDiscount = function (state) {
    return selectActiveDiscountEventInfoByEventType(state, EventType.ExpansionDiscount);
};
export var selectHighlightedExpansionId = function (state) {
    var selectedBuilding = state.playerBase.buildings.building;
    return selectedBuilding ? -1 : state.playerBase.expansions.highlightedExpansionId;
};



//////////////////
// WEBPACK FOOTER
// ./src/selectors/selectExpansionInfo.ts
// module id = 161
// module chunks = 3